import { createApi as createApi$1 } from '@reduxjs/toolkit/query/react';

const assetTypes = {
  channel: "LIVE",
  episode: "EPISODE",
  liveEpisode: "LIVE_EPISODE",
  liveMovie: "LIVE_MOVIE",
  liveSerie: "LIVE_SERIE",
  liveShow: "LIVE_SHOW",
  liveSport: "LIVE_SPORT",
  movie: "MOVIE",
  serie: "SERIE",
  show: "SHOW",
  sport: "SPORT"
};
const myListCarouselId = "UCTAMyListVODMoviesSeries";
const rtkQueryTags = {
  aem: "AEM",
  thinkAnalytics: "ThinkAnalytics",
  userCustomData: "UserCustomData"
};
const rtkQueryTagsIds = {
  myList: "MyList"
};
const transformAssetType = ({ asset, isLive }) => {
  const assetType = asset.type || asset.programType;
  if (!assetType)
    return asset;
  let newAssetType = assetType.toUpperCase();
  if (isLive && newAssetType !== assetTypes.channel) {
    const capitalizedAssetType = newAssetType.toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
    newAssetType = assetTypes[`live${capitalizedAssetType}`];
  }
  const newAsset = { ...asset };
  if (asset.type)
    newAsset.type = newAssetType;
  if (asset.programType)
    newAsset.programType = newAssetType;
  return newAsset;
};
const createApi = (backendService, forgeRockService, aemService) => createApi$1({
  baseQuery: async (service) => {
    try {
      const response = await service();
      return { data: response.data };
    } catch (error) {
      console.error("Error while fetching data", error);
      return {
        error: {
          data: error.response?.data || error,
          status: error.response?.status
        }
      };
    }
  },
  tagTypes: Object.values(rtkQueryTags),
  endpoints: (build) => ({
    addToMyList: build.mutation({
      query: ({ id, type }) => () => backendService.experience.v3.addToMyList({ id, type }),
      invalidatesTags: [{ type: rtkQueryTags.thinkAnalytics, id: rtkQueryTagsIds.myList }]
    }),
    addToMyListV4: build.mutation({
      query: (addToMyListParams) => () => backendService.experience.v4.addToMyList(
        addToMyListParams
      ),
      invalidatesTags: [{ type: rtkQueryTags.thinkAnalytics, id: rtkQueryTagsIds.myList }]
    }),
    getAssetDetail: build.query({
      query: ({ assetId }) => () => backendService.content.v1.getAssetDetail(assetId)
    }),
    getCurableBanner: build.query({
      query: ({ bannerId }) => () => backendService.layout.v4.getCurableBanner(bannerId)
    }),
    getCarousel: build.query({
      query: ({
        source,
        sourceCarouselId,
        type,
        filters,
        max
      }) => () => backendService.layout.v3.getCarousel(
        { source, sourceCarouselId, type },
        filters,
        { max }
      ),
      providesTags: (result, _, { type }) => {
        if (result?.id === myListCarouselId || type === "my-list") {
          return [{ type: rtkQueryTags.thinkAnalytics, id: rtkQueryTagsIds.myList }];
        }
        if (result?.source !== "ThinkAnalytics")
          return [];
        return [rtkQueryTags.thinkAnalytics];
      },
      transformResponse: ({ contents, sourceCarouselId: id, ...carousel }) => ({
        ...carousel,
        contents: contents?.map((asset) => transformAssetType({ asset, isLive: asset.isLive })),
        id
      })
    }),
    getChannels: build.query({
      query: ({ assetToken }) => () => backendService.content.v2.getChannels(assetToken)
    }),
    getContinueWatchingData: build.query({
      queryFn: async ({ assetId, assetType, vmsId }, baseQueryApi, extraOptions, baseQuery) => {
        let params;
        let service;
        switch (assetType) {
          case "series": {
            params = assetId;
            service = backendService.experience.v3.getContinueWatchingShow;
            break;
          }
          case "movies": {
            params = { vmsId, vrioAssetId: assetId };
            service = backendService.experience.v3.getContinueWatchingMovie;
            break;
          }
          default: {
            return {
              error: {
                data: "No valid assetType specified"
              }
            };
          }
        }
        const response = await baseQuery(() => service(params));
        return { data: response.data };
      },
      providesTags: [rtkQueryTags.thinkAnalytics]
    }),
    getMostWatchedChannels: build.query({
      query: () => backendService.experience.v3.getMostwatchedChannels,
      providesTags: [rtkQueryTags.thinkAnalytics]
    }),
    getMyList: build.query({
      query: () => backendService.experience.v3.getMyList,
      providesTags: [{ type: rtkQueryTags.thinkAnalytics, id: rtkQueryTagsIds.myList }]
    }),
    getPage: build.query({
      query: ({ id, sportIds }) => () => backendService.layout.v3.getPage(id, sportIds),
      transformResponse: ({ carousels: elements }) => ({
        elements: elements.map(({
          sourceCarouselId: id,
          ...element
        }) => ({ ...element, id }))
      })
    }),
    getPageV4: build.query({
      query: ({ id, sportIds }) => () => backendService.layout.v4.getPage(id, sportIds),
      transformResponse: ({ elements }) => ({
        elements: elements.map(({
          elementId: id,
          ...element
        }) => ({ ...element, id }))
      })
    }),
    getRecentlyWatchedChannels: build.query({
      query: () => backendService.experience.v3.getRecentlyWatchedChannels,
      providesTags: [rtkQueryTags.thinkAnalytics]
    }),
    getSchedules: build.query({
      query: ({
        channelId,
        startTime,
        endTime,
        pageNumber,
        pageSize,
        assetToken
      }) => () => backendService.content.v2.getSchedules({
        channelId,
        startTime,
        endTime,
        pageNumber,
        pageSize,
        assetToken
      }),
      transformResponse: (response) => (response.channels || response).map(({ schedules, ...channel }) => ({
        ...channel,
        schedules: schedules.map((schedule) => transformAssetType({
          asset: schedule,
          isLive: true
        }))
      }))
    }),
    getSchedulesByEventType: build.query({
      query: ({
        channelId,
        eventType,
        pageSize,
        assetToken
      }) => () => backendService.content.v2.getSchedulesByEventType(channelId, {
        eventType,
        pageSize,
        assetToken
      }),
      transformResponse: (response) => (response.channels || response).map(({ schedules, ...channel }) => ({
        ...channel,
        schedules: schedules.map((schedule) => transformAssetType({
          asset: schedule,
          isLive: true
        }))
      }))
    }),
    getSeasonDetail: build.query({
      query: ({ assetId, seasonNumber }) => () => backendService.content.v1.getSeasonDetail(
        assetId,
        seasonNumber
      )
    }),
    getSeriesDetail: build.query({
      query: ({ assetId }) => () => backendService.content.v1.getShowDetail(assetId)
    }),
    removeFromMyList: build.mutation({
      query: ({ id, type }) => () => backendService.experience.v3.removeFromMyList({ id, type }),
      invalidatesTags: [{ type: rtkQueryTags.thinkAnalytics, id: rtkQueryTagsIds.myList }]
    }),
    searchAssets: build.query({
      query: (searchAssetsParams) => () => backendService.content.v1.searchAssets(searchAssetsParams),
      transformResponse: ({ contents, ...searchResult }) => ({
        ...searchResult,
        contents: contents?.map((asset) => transformAssetType({
          asset,
          isLive: asset.contentSourceId === 1
        }))
      })
    }),
    getCarouselInsight: build.query({
      query: ({
        source,
        sourceCarouselId,
        type,
        filters,
        max,
        recommendationType
      }) => () => backendService.layout.v4.getCarouselInsight(
        { source, sourceCarouselId, type },
        recommendationType,
        filters,
        { max }
      ),
      providesTags: (result, _, { type }) => {
        if (result?.id === myListCarouselId || type === "my-list") {
          return [{ type: rtkQueryTags.thinkAnalytics, id: rtkQueryTagsIds.myList }];
        }
        return [];
      },
      transformResponse: ({ contents, sourceCarouselId: id, ...carousel }) => ({
        ...carousel,
        contents: contents?.map((asset) => transformAssetType({ asset, isLive: asset.isLive })),
        id
      })
    }),
    getCarouselGeneral: build.query({
      query: ({
        source,
        sourceCarouselId,
        type,
        filters,
        max,
        recommendationType
      }) => () => backendService.layout.v4.getCarouselGeneral(
        { source, sourceCarouselId, type },
        recommendationType,
        filters,
        { max }
      ),
      providesTags: (result, _, { type }) => {
        if (result?.id === myListCarouselId || type === "my-list") {
          return [{ type: rtkQueryTags.thinkAnalytics, id: rtkQueryTagsIds.myList }];
        }
        return [];
      },
      transformResponse: ({ contents, sourceCarouselId: id, ...carousel }) => ({
        ...carousel,
        contents: contents?.map((asset) => transformAssetType({ asset, isLive: asset.isLive })),
        id
      })
    }),
    getPageV5: build.query({
      query: ({ id, sportIds }) => () => backendService.layout.v5.getPage(id, sportIds),
      transformResponse: ({ elements }) => ({
        elements: elements.map(({
          elementId: id,
          ...element
        }) => ({ ...element, id }))
      })
    }),
    getMoreLikeThisVod: build.query({
      query: ({
        contentId,
        max
      }) => () => backendService.content.v3.getMoreLikeThisVod(contentId, max)
    }),
    getMoreLikeThisEpg: build.query({
      query: ({
        contentId,
        max
      }) => () => backendService.content.v3.getMoreLikeThisEpg(contentId, max)
    }),
    // ForgeRock Endpoints
    getUserCustomData: build.query({
      query: (iso2Code) => () => forgeRockService.userCustomData.getUserCustomData(iso2Code),
      providesTags: [rtkQueryTags.userCustomData]
    }),
    updateUserCustomData: build.mutation({
      query: ({ iso2Code, customData }) => () => forgeRockService.userCustomData.updateUserCustomData(iso2Code, customData),
      invalidatesTags: [rtkQueryTags.userCustomData]
    }),
    getSSOToken: build.query({
      query: (params) => () => {
        if (typeof params === "string") {
          return forgeRockService.login.getSSOToken(params);
        }
        return forgeRockService.login.getSSOToken(params.idToken, params.authIndexValue);
      }
    }),
    registerDevice: build.query({
      query: ({ registrationCode, ssoToken }) => () => forgeRockService.login.registerDevice(registrationCode, ssoToken)
    }),
    // AEM Endpoints
    getAEMConfiguration: build.query({
      query: (platform) => () => aemService?.config?.getConfig(platform),
      providesTags: [rtkQueryTags.aem]
    }),
    getIntersitialConfiguration: build.query({
      query: () => aemService?.interstitial?.getInterstitial
    }),
    getAllTermsOfServiceByCountry: build.query({
      query: () => aemService?.termsOfService?.getAllTermsOfServiceByCountry
    })
  })
});

export { assetTypes, createApi as default, rtkQueryTags, rtkQueryTagsIds };
//# sourceMappingURL=index.js.map
